// src/features/tidsregistering/components/attendance/HourViewHeader.jsx
import React, { memo } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';

const HOUR_ROW_HEIGHT = 36;
const TICK_ROW_HEIGHT = 24;
export const HOUR_VIEW_HEADER_HEIGHT = HOUR_ROW_HEIGHT + TICK_ROW_HEIGHT;

/**
 * Two-row header for the hour-granularity attendance timeline.
 *
 * Row 1: hour labels (00, 01, …, 23), each spanning slotCount columns.
 * Row 2: 15-min tick marks (:00, :15, :30, :45) under each hour.
 *
 * Props mirror what TimelineGrid passes to renderHeader:
 *   visibleDayIndices — visible slot indices (treated as "day" indices by TimelineGrid)
 *   dayWidth          — px per slot column
 *
 * Plus domain-specific props passed via closure from AttendanceChart:
 *   hourGroups        — [{ hour, label, startSlot, slotCount }] from useHourViewLogic
 *   slotMinutes       — minutes per slot (default 15)
 */
function HourViewHeader({ visibleDayIndices, dayWidth, hourGroups, slotMinutes = 15, totalSlots = Infinity }) {
  const theme = useTheme();

  if (!visibleDayIndices.length) return null;

  const firstSlot = visibleDayIndices[0];
  const lastSlot = visibleDayIndices[visibleDayIndices.length - 1];
  const slotsPerHour = 60 / slotMinutes;

  const visibleHours = hourGroups.filter(
    (g) => g.startSlot + g.slotCount > firstSlot && g.startSlot <= lastSlot,
  );

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        height: HOUR_VIEW_HEADER_HEIGHT,
        bgcolor: 'background.paper',
        borderBottom: `2px solid ${theme.palette.divider}`,
        zIndex: 10,
      }}
    >
      {/* Hour labels row */}
      <Box
        sx={{
          height: HOUR_ROW_HEIGHT,
          position: 'relative',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.grey[800],
        }}
      >
        {visibleHours.map(({ hour, label, startSlot, slotCount }) => (
          <Box
            key={hour}
            sx={{
              position: 'absolute',
              left: (startSlot - firstSlot) * dayWidth,
              width: slotCount * dayWidth,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRight: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: 12, fontWeight: 600, color: 'common.white', lineHeight: 1 }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Minute tick row */}
      <Box sx={{ height: TICK_ROW_HEIGHT, position: 'relative' }}>
        {visibleDayIndices.filter((slotIdx) => slotIdx < totalSlots).map((slotIdx, i) => {
          const slotWithinHour = slotIdx % slotsPerHour;
          const minuteMark = slotWithinHour * slotMinutes;
          const isHourBoundary = minuteMark === 0;

          return (
            <Box
              key={slotIdx}
              sx={{
                position: 'absolute',
                left: i * dayWidth,
                width: dayWidth,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderLeft: `1px solid ${
                  isHourBoundary
                    ? theme.palette.divider
                    : alpha(theme.palette.divider, 0.4)
                }`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: 9,
                  color: isHourBoundary ? 'text.primary' : 'text.disabled',
                  lineHeight: 1,
                  userSelect: 'none',
                }}
              >
                {isHourBoundary ? '00' : `:${String(minuteMark).padStart(2, '0')}`}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default memo(HourViewHeader);
